import React from 'react'
import { Container, Header, Divider } from 'semantic-ui-react'

import SEO from 'lib/components/SEO'
import ExternalLink from 'lib/components/ExternalLink'

import SpeedGraph from './SpeedGraph'
import CTA from './CTA'
import Section from './Section'

const Microbiological = ({}) => {
  return (
    <>
      <SEO
        title="Microbiological recalls can cause food poisoning and worse if consumed | Recall Monitor"
        description="Products that are recalled for microbiological contaminants can cause food poisoninng and worse effects. Learn about the common microbiological threats from 70+ recalled products in Canada."
        canonical="https://recallmonitor.ca/why/microbiological"
        type="article"
        image="https://recallmonitor.ca/static/why/botulism.jpg"
      />
      <Container text css={{ margin: '1rem 0' }}>
        <Header as="h1">
          Food Poisoning
          <Header.Subheader>
            Avoid microbiological threats to your health
          </Header.Subheader>
        </Header>
        <p>
          Every year hundreds of products are recalled because they contain
          microbiological threats. These can cause severe symptoms and result in
          mass outbreaks that affect everyone negatively. Since 2019 there has
          been <strong>197</strong> products that can put your health at risk.
          That is a product recall every <strong>3 days</strong>.
        </p>

        <p>
          How can consumers keep themselves protected? How can restaurants,
          cafes and food businesses ensure they are safe from contaminants in
          foods they serve?{' '}
          <strong>
            Just a few hours of delay might mean unknown exposure to these
            bacteria and viruses.
          </strong>
        </p>

        <SpeedGraph />

        <p>
          Recall Monitor can help you keep track of active recalls for
          contaminants, keeping you and your customers safe from food poisoning.
        </p>

        <p>
          Businesses can serve their customers better and avoid costly liability
          issues by protecting themselves against recalls on these foods.
          Oversights can cause huge issues, like the{' '}
          <ExternalLink href="https://bc.ctvnews.ca/chicken-served-in-restaurants-hospitals-and-nursing-homes-could-be-contaminated-with-listeria-1.4556141">
            Listeria infections involving three provinces: British Columbia,
            Manitoba and Ontario
          </ExternalLink>{' '}
          from food served in restaurants, hospitals and nursing homes. Since
          2019:
        </p>

        <Divider />

        <Section img="/static/why/salmonella.jpg">
          <Header>Salmonella</Header>
          <p>
            <strong>42</strong> products were recalled because they were
            contaminated with salmonella.
          </p>
          <p>
            Signs and symptoms of salmonella infection generally last two to
            seven days. Diarrhea may last up to 10 days, although it may take
            several months before bowels return to normal. In some cases, the
            diarrhea associated with salmonella infection can be so dehydrating
            as to require prompt medical attention. Life-threatening
            complications also may develop if the infection spreads beyond your
            intestines.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/listeria.jpeg" reversed>
          <Header>Listeria</Header>
          <p>
            <strong>60</strong> products contaminated with listeria.
          </p>
          <p>
            Listeria infection is a foodborne bacterial illness that can be very
            serious for pregnant women and people with impaired immune systems.
            The disease can be fatal to unborn babies and newborns.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/botulism.jpg">
          <Header>Botulism</Header>
          <p>
            <strong>10</strong> products that contained the Clostridium
            botulinum bacteria.
          </p>
          <p>
            Botulism is a life-threatening disease caused by the ingestion of a
            potent neurotoxin produced during growth of the C. botulinum
            bacteria. This neurotoxin is among the most toxic substances known;
            even microscopic amounts can cause illness or death.
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/ecoli.jpg" reversed>
          <Header>E. coli</Header>
          <p>
            <strong>43</strong> cases of recalled products from E.coli.
          </p>
          <p>
            People who get infected with E. coli can have a wide range of
            symptoms. You may not get sick at all. If you do get sick, you may
            experience: nausea, vomiting, headache, mild fever, severe stomach
            cramps and watery or bloody diarrhea
          </p>
        </Section>

        <Divider />

        <Section img="/static/why/other-mb.jpg">
          <Header>Other</Header>
          <p>
            <strong>42</strong> products with other microbiological hazards.
          </p>
        </Section>

        <Divider />

        <Header>The list goes on...</Header>
        <p>
          Were you aware of any products recalled because of microbiological
          contaminants?
        </p>
        <p>
          Recall Monitor will help you get knowledgeable about recalled
          products. Don't be at the mercy of a news segment you may never see.
          Sign up now, and start receiving the alerts you care about -
          instantly.
        </p>
        <CTA />
      </Container>
    </>
  )
}

export default Microbiological
